<template>
  <div class="api-usage page p-2">
    <page-header class="mb-2" :title="$t('API usage')">
      <template v-slot:buttons>
        <b-button variant="primary" :disabled="loading" @click="onExport">
          <font-awesome-icon icon="download" class="mr-1"/>
          {{ $t('Export XLSX') }}
        </b-button>
      </template>
    </page-header>

    <general-loader v-if="loading && !report" :loading="true"/>

    <div v-else class="card content-wrapper p-2">
      <div v-if="report" class="row mb-3">
        <div class="col-md-3 mb-2 mb-md-0">
          <amount-viewer
              :title="$t('Current cycle')"
              :amount="cycleLabel"
              icon="calendar-alt"
              text-class="text-info"/>
        </div>
        <div class="col-md-3 mb-2 mb-md-0">
          <amount-viewer
              :title="$t('Requests')"
              :amount="report.totals.request_count"
              icon="bolt"
              text-class="text-primary"/>
        </div>
        <div class="col-md-3 mb-2 mb-md-0">
          <amount-viewer
              :title="$t('Bandwidth out')"
              :amount="bandwidthLabel"
              icon="cloud-upload-alt"
              text-class="text-success"/>
        </div>
        <div class="col-md-3">
          <amount-viewer
              :title="$t('Cost') + ' (' + report.currency + ')'"
              :amount="formatMoney(report.totals.cost)"
              icon="file-invoice-dollar"
              text-class="text-warning"/>
        </div>
      </div>

      <b-form class="api-usage-filters mb-3" @submit.prevent="onSearch">
        <b-form-group class="mb-0 api-usage-filters__field" :label="$t('Date range')">
          <date-range-picker
              v-model="dateRange"
              class="api-usage-filters__daterange"
              showDropdowns
              appendToBody
              opens="right"
              :max-date="maxDate"
              :locale-data="{ firstDay: 1, format: 'DD-MM-YYYY' }"
              @update="onDateRangeApply">
            <template v-slot:input="picker">
              <span v-if="!picker.startDate" class="text-muted">{{ $t('Date range') }}</span>
              <span v-else>{{ picker.startDate | toDate }} - {{ picker.endDate | toDate }}</span>
            </template>
          </date-range-picker>
        </b-form-group>
        <b-form-group class="mb-0 api-usage-filters__field" :label="$t('User')">
          <b-form-select v-model="filters.user" :options="userOptions"/>
        </b-form-group>
        <b-form-group class="mb-0 api-usage-filters__field" :label="$t('Method')">
          <b-form-select v-model="filters.method" :options="methodOptions"/>
        </b-form-group>
        <b-form-group class="mb-0 api-usage-filters__field api-usage-filters__field--route" :label="$t('Route')">
          <b-form-select v-model="filters.route" :options="routeOptions"/>
        </b-form-group>
        <div class="api-usage-filters__buttons">
          <b-button type="submit" variant="primary">{{ $t('Apply') }}</b-button>
          <b-button type="button" variant="secondary" @click="onReset">{{ $t('This cycle') }}</b-button>
        </div>
      </b-form>

      <h5 class="mb-2">{{ $t('Billing periods') }}</h5>
      <b-table
          class="mb-4"
          :items="report ? report.periods : []"
          :fields="periodFields"
          :busy="loading"
          small
          bordered
          show-empty>
        <template v-slot:cell(is_current)="{ item }">
          {{ item.is_current ? $t('Current') : '' }}
        </template>
        <template v-slot:cell(is_paid)="{ item }">
          {{ item.is_paid ? $t('Paid') : $t('Unpaid') }}
        </template>
        <template v-slot:cell(amount_due)="{ item }">
          {{ formatMoney(item.amount_due) }}
        </template>
        <template v-slot:cell(allocated_cost)="{ item }">
          {{ formatMoney(item.allocated_cost) }}
        </template>
      </b-table>

      <h5 class="mb-2">{{ $t('Usage') }}</h5>
      <b-table
          :items="report ? report.rows : []"
          :fields="rowFields"
          :busy="loading"
          small
          bordered
          show-empty
          stacked="md">
        <template v-slot:cell(user)="{ item }">
          {{ item.user || $t('Unattributed') }}
        </template>
        <template v-slot:cell(request_cost)="{ item }">
          {{ formatMoney(item.request_cost) }}
        </template>
        <template v-slot:cell(bandwidth_cost)="{ item }">
          {{ formatMoney(item.bandwidth_cost) }}
        </template>
        <template v-slot:cell(cost)="{ item }">
          {{ formatMoney(item.cost) }}
        </template>
      </b-table>
    </div>
  </div>
</template>

<script>
  import PageHeader from "@/core/components/page-header/PageHeader";
  import GeneralLoader from "@/core/components/general-loader/GeneralLoader";
  import AmountViewer from "@/core/components/common/AmountViewer";
  import ApiUsageService from "@/modules/api-usage/ApiUsageService";
  import DateRangePicker from 'vue2-daterange-picker'
  import 'vue2-daterange-picker/dist/vue2-daterange-picker.css'

  export default {
    name: "ApiUsage",
    components: {PageHeader, GeneralLoader, AmountViewer, DateRangePicker},
    data() {
      return {
        loading: false,
        report: null,
        filterOptions: {users: [], methods: [], routes: []},
        dateRange: {startDate: null, endDate: null},
        filters: {
          date_from: null,
          date_to: null,
          user: '',
          method: '',
          route: '',
        },
        periodFields: [
          {key: 'period_start', label: this.$t('Start')},
          {key: 'period_end', label: this.$t('End')},
          {key: 'is_current', label: this.$t('Status')},
          {key: 'is_paid', label: this.$t('Paid')},
          {key: 'request_count', label: this.$t('Requests')},
          {key: 'gb_out', label: this.$t('GB out')},
          {key: 'allocated_cost', label: this.$t('Filtered cost')},
          {key: 'amount_due', label: this.$t('Period amount')},
        ],
        rowFields: [
          {key: 'period', label: this.$t('Date')},
          {key: 'cycle', label: this.$t('Cycle')},
          {key: 'user', label: this.$t('User')},
          {key: 'method', label: this.$t('Method')},
          {key: 'route_pattern', label: this.$t('Route')},
          {key: 'request_count', label: this.$t('Requests')},
          {key: 'bytes_out', label: this.$t('Bytes out')},
          {key: 'gb_out', label: this.$t('GB out')},
          {key: 'request_cost', label: this.$t('Request cost')},
          {key: 'bandwidth_cost', label: this.$t('Bandwidth cost')},
          {key: 'cost', label: this.$t('Cost')},
        ],
      }
    },
    computed: {
      maxDate() {
        const date = new Date();
        date.setHours(23, 59, 59, 999);
        return date;
      },
      cycleLabel() {
        if (!this.report || !this.report.current_period) {
          return '';
        }
        return `${this.report.current_period.start} → ${this.report.current_period.end}`;
      },
      bandwidthLabel() {
        if (!this.report) {
          return '';
        }
        return `${this.report.totals.gb_out} GB`;
      },
      userOptions() {
        return [
          {value: '', text: this.$t('All users')},
          ...this.filterOptions.users.map(user => ({value: user.uuid, text: user.email})),
        ];
      },
      methodOptions() {
        return [
          {value: '', text: this.$t('All methods')},
          ...this.filterOptions.methods.map(method => ({value: method, text: method})),
        ];
      },
      routeOptions() {
        return [
          {value: '', text: this.$t('All routes')},
          ...this.filterOptions.routes.map(route => ({value: route, text: route})),
        ];
      },
    },
    methods: {
      formatMoney(value) {
        const amount = Number(value || 0);
        return amount.toFixed(4);
      },
      formatDate(value) {
        if (!value) {
          return null;
        }
        if (value.format) {
          return value.format('YYYY-MM-DD');
        }
        const date = value.toDate ? value.toDate() : new Date(value);
        const year = date.getFullYear();
        const month = String(date.getMonth() + 1).padStart(2, '0');
        const day = String(date.getDate()).padStart(2, '0');
        return `${year}-${month}-${day}`;
      },
      onDateRangeApply() {
        const today = new Date();
        today.setHours(0, 0, 0, 0);
        const endValue = this.dateRange && this.dateRange.endDate;
        if (endValue) {
          const endDate = endValue.toDate ? endValue.toDate() : new Date(endValue);
          if (endDate > today) {
            this.dateRange = {
              startDate: this.dateRange.startDate,
              endDate: today,
            };
          }
        }
        this.filters.date_from = this.formatDate(this.dateRange.startDate);
        this.filters.date_to = this.formatDate(this.dateRange.endDate);
      },
      async loadFilters() {
        const response = await ApiUsageService.getFilters();
        if (response.success) {
          this.filterOptions = response.body;
        }
      },
      async loadReport(resetDates = false) {
        this.loading = true;
        const params = resetDates ? {} : {
          date_from: this.filters.date_from,
          date_to: this.filters.date_to,
          user: this.filters.user,
          method: this.filters.method,
          route: this.filters.route,
        };
        const response = await ApiUsageService.getReport(params);
        this.loading = false;
        if (!response.success) {
          return;
        }
        this.report = response.body;
        this.filters.date_from = response.body.filters.date_from;
        this.filters.date_to = response.body.filters.date_to;
        this.dateRange = {
          startDate: response.body.filters.date_from,
          endDate: response.body.filters.date_to,
        };
      },
      onSearch() {
        this.onDateRangeApply();
        this.loadReport(false);
      },
      onReset() {
        this.filters.user = '';
        this.filters.method = '';
        this.filters.route = '';
        this.loadReport(true);
      },
      async onExport() {
        this.onDateRangeApply();
        const response = await ApiUsageService.exportXlsx({
          date_from: this.filters.date_from,
          date_to: this.filters.date_to,
          user: this.filters.user,
          method: this.filters.method,
          route: this.filters.route,
        });
        if (!response.success) {
          return;
        }
        const blob = new Blob([response.body], {
          type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
        });
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        const dateFrom = this.filters.date_from || 'start';
        const dateTo = this.filters.date_to || 'end';
        link.setAttribute('download', `api-usage-${dateFrom}-to-${dateTo}.xlsx`);
        document.body.appendChild(link);
        link.click();
        link.remove();
        window.URL.revokeObjectURL(url);
      }
    },
    async mounted() {
      await this.loadFilters();
      await this.loadReport(true);
    }
  }
</script>

<style lang="scss" scoped>
  .api-usage-filters {
    display: flex;
    flex-wrap: wrap;
    align-items: flex-end;
    gap: 12px;

    ::v-deep .form-group {
      margin-bottom: 0;
    }

    &__field {
      min-width: 160px;
    }

    &__field--route {
      flex: 1 1 220px;
      min-width: 220px;
    }

    &__daterange {
      display: block;
      width: 100%;
    }

    ::v-deep .reportrange-text {
      display: flex;
      align-items: center;
      height: calc(1.5em + 0.75rem + 2px);
      min-width: 220px;
      padding: 0.375rem 0.75rem;
      border: 1px solid #ced4da;
      border-radius: 0.25rem;
      background: #fff;
      line-height: 1.5;
    }

    ::v-deep .custom-select,
    ::v-deep .form-control {
      height: calc(1.5em + 0.75rem + 2px);
    }

    &__buttons {
      display: flex;
      align-items: center;
      gap: 8px;
      height: calc(1.5em + 0.75rem + 2px);
    }

    &__buttons .btn {
      height: calc(1.5em + 0.75rem + 2px);
      padding-top: 0;
      padding-bottom: 0;
      display: inline-flex;
      align-items: center;
    }
  }
</style>
